/**
 * 统一审计日志的 mock 数据源。
 *
 * <p>全站只有一个审计留痕存储（此前 system-config / role / user / landingPage 四份 mock 各写各的
 * localStorage 键，页面也各读各的）。各域的 mock 写操作经自己的 helper 汇到这里的
 * {@link recordMockAudit}，读侧（统一审计页）只认这一份。
 *
 * <p>字典的动作标签是后端 {@code AuditAction} 枚举的一份**子集镜像**，只覆盖 mock 行里会出现的
 * code；缺失时前端按原始 code 展示，不影响真实后端模式（那时字典唯一来自
 * {@code GET /api/admin/audit-logs/dictionary}）。
 */
import { generateId, mockDelay } from '@/services/types'
import { isMockEnabled } from '@/utils/useMock'
import { toLocalDateTimeParam } from '@/utils/formatters'
import type { PaginatedResponse } from '@/services/types'
import type {
  AuditActionOption,
  AuditDictionary,
  AuditLogItem,
  AuditLogQuery,
  AuditModuleOption,
  AuditResult,
} from '@/types/audit'

const AUDIT_KEY = 'audit_logs'

/** mock 侧最多保留的行数，防止长时间点击把 localStorage 撑满。 */
const MAX_ROWS = 2000

const MOCK_MODULES: AuditModuleOption[] = [
  { code: 'doc', label: '文档' },
  { code: 'config', label: '系统配置' },
  { code: 'content', label: '首页内容' },
  { code: 'org', label: '单位管理' },
  { code: 'role', label: '角色权限' },
  { code: 'user', label: '用户' },
]

const MOCK_ACTION_LABELS: Record<string, string> = {
  create: '新增',
  update: '修改',
  delete: '删除',
  view: '查看',
  clone: '复制',
  reorder: '调整顺序',
  publish: '发布',
  discard_draft: '放弃草稿',
  upload: '上传',
  download: '下载',
  export: '导出',
  import: '导入',
  batch_import: '批量导入',
  sync: '同步',
  favorite: '收藏',
  share: '分享',
  chat: 'AI 问答',
  status_change: '状态变更',
  role_assign: '角色变更',
  permission_assign: '配置权限',
  assign_users: '分配用户到角色',
  remove_users: '从角色移除用户',
  password_reset: '重置密码',
  force_logout: '强制下线',
  member_add: '添加成员',
  member_remove: '移除成员',
  org_transfer: '单位迁移',
  auto_overdue: '到期自动逾期',
}

/** 各域 mock 写操作汇入统一留痕时携带的字段。 */
export interface MockAuditSeed {
  module: string
  action: string
  targetType?: string | null
  targetId?: string | null
  targetName?: string | null
  summary?: string | null
  operatorId?: string | null
  operatorName?: string | null
  ipAddress?: string | null
  result?: AuditResult
  errorMessage?: string | null
  /** 传对象也可：{@link jsonOf} 会序列化，与后端 jsonb 载荷（对象文本）同形态。 */
  beforePayload?: unknown
  afterPayload?: unknown
  /** 缺省取当前时间；种子数据用它铺出一条几天的历史。 */
  createdAt?: string
}

/**
 * 铺底演示数据：六个模块各有权重，含失败留痕、无登录态的 system 行、有/无快照、非 JSON 快照。
 * 只在本地没有留痕时写入，因此反复刷新不会越积越多；要重新铺底清掉 localStorage 的 audit_logs。
 */
const DEMO_SEEDS: MockAuditSeed[] = [
  {
    module: 'doc',
    action: 'upload',
    targetType: 'document',
    targetId: 'doc-1001',
    targetName: '2026年第三季度财务报表.pdf',
    summary: '上传文档',
    ipAddress: '192.168.1.24',
    createdAt: '2026-10-08T09:12:30',
  },
  {
    module: 'doc',
    action: 'delete',
    targetType: 'document',
    targetId: 'doc-0932',
    targetName: '作废-旧版报销单.docx',
    summary: '删除文档',
    result: 'failed',
    errorMessage: '文档已被他人引用，不允许删除',
    createdAt: '2026-10-07T18:02:11',
  },
  {
    module: 'config',
    action: 'update',
    targetType: 'basic_settings',
    targetId: 'system_name',
    targetName: '系统名称',
    summary: '修改系统名称',
    beforePayload: { systemName: '台账助手' },
    afterPayload: { systemName: '智台账' },
    createdAt: '2026-10-06T10:40:05',
  },
  {
    module: 'config',
    action: 'update',
    targetType: 'security_policy',
    targetId: 'password_policy',
    targetName: '密码策略',
    summary: '提高密码最小长度',
    beforePayload: 'minLength=8（旧实现直接存的文本，不是合法 JSON）',
    afterPayload: '{"minLength":10}',
    createdAt: '2026-10-05T14:22:41',
  },
  {
    module: 'content',
    action: 'publish',
    targetType: 'section',
    targetId: 'hero',
    targetName: '首屏 Hero 区',
    summary: '发布首页内容',
    afterPayload: { title: '让台账自己跑起来' },
    createdAt: '2026-10-04T11:00:00',
  },
  {
    module: 'org',
    action: 'member_add',
    targetType: 'organization_member',
    targetId: 'org-2001:u-3003',
    targetName: '滨江街道办事处 / 李文',
    summary: '添加成员',
    createdAt: '2026-10-03T16:35:20',
  },
  {
    module: 'role',
    action: 'permission_assign',
    targetType: 'role',
    targetId: 'role-org-admin',
    targetName: '单位管理员',
    summary: '配置权限',
    beforePayload: { permissions: ['ledger:view'] },
    afterPayload: { permissions: ['ledger:view', 'ledger:edit'] },
    createdAt: '2026-10-02T09:50:12',
  },
  {
    module: 'user',
    action: 'password_reset',
    targetType: 'user',
    targetId: 'u-3011',
    targetName: '赵敏',
    summary: '重置登录密码',
    createdAt: '2026-10-01T15:10:48',
  },
  {
    module: 'user',
    action: 'batch_import',
    targetName: '批量导入用户 32 条',
    summary: '批量导入',
    afterPayload: { success: 30, failed: 2 },
    createdAt: '2026-09-30T10:05:00',
  },
  {
    module: 'config',
    action: 'auto_overdue',
    targetType: 'subscription',
    targetId: 'sub-77',
    targetName: '专业版年付订阅',
    summary: '订阅到期，由定时任务自动置为逾期',
    operatorId: null,
    operatorName: 'system',
    ipAddress: '0.0.0.0',
    createdAt: '2026-09-29T03:00:00',
  },
]

// 铺底演示数据只在 mock 模式进行：本模块会被 landing 等 service 静态引用，
// 真实后端模式下若照常铺底会往 localStorage 写入无人读取的演示行。
if (isMockEnabled) seedMockAuditLogs(DEMO_SEEDS)

function jsonOf(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

function toRow(seed: MockAuditSeed): AuditLogItem {
  return {
    id: generateId(),
    createdAt: seed.createdAt ?? toLocalDateTimeParam(new Date()),
    module: seed.module,
    action: seed.action,
    targetType: seed.targetType ?? null,
    targetId: seed.targetId ?? null,
    targetName: seed.targetName ?? null,
    operatorId: seed.operatorId ?? 'mock-admin-id',
    operatorName: seed.operatorName ?? '系统管理员',
    organizationId: 'mock-default-org',
    ipAddress: seed.ipAddress ?? '127.0.0.1',
    result: seed.result ?? 'success',
    errorMessage: seed.errorMessage ?? null,
    summary: seed.summary ?? null,
    beforePayload: jsonOf(seed.beforePayload),
    afterPayload: jsonOf(seed.afterPayload),
  }
}

function readRows(): AuditLogItem[] {
  try {
    const raw = localStorage.getItem(AUDIT_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) ? (parsed as AuditLogItem[]) : []
  } catch {
    return []
  }
}

function writeRows(rows: AuditLogItem[]): void {
  try {
    localStorage.setItem(AUDIT_KEY, JSON.stringify(rows.slice(0, MAX_ROWS)))
  } catch {
    // mock 存储写满不影响业务演示
  }
}

/** 写入一条 mock 留痕（时间倒序插在最前）。 */
export function recordMockAudit(seed: MockAuditSeed): void {
  const rows = readRows()
  rows.unshift(toRow(seed))
  writeRows(rows)
}

/** 供 mock 写侧批量落种子：已存在则不重复铺，避免刷新后越积越多。 */
export function seedMockAuditLogs(seeds: MockAuditSeed[]): void {
  if (localStorage.getItem(AUDIT_KEY)) return
  writeRows(seeds.map(toRow))
}

/**
 * 与后端 {@code AuditLogSpecifications.parseDateTime} 同口径：长度 ≤10 视为纯日历日，补当日首末秒。
 * 不补齐时按字典序比较，endDate='2026-10-09' 会把当天全部留痕滤掉（后端不会，mock 也就不该）。
 *
 * <p>布尔位写成「是否为区间下界」而非 startOfDay：下界补 00:00:00、上界补 23:59:59，
 * 传反会让区间正好扣掉它声称包含的那一天（由 audit-mock-filter.spec.ts 钉住）。
 */
function toDayBoundary(value: string | undefined, isLowerBound: boolean): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return trimmed.length <= 10 ? `${trimmed}T${isLowerBound ? '00:00:00' : '23:59:59'}` : trimmed
}

/**
 * 与后端同一套筛选口径：模块多选、动作单选、操作人/对象、关键字模糊、时间区间。
 *
 * 独立导出是为了让单测能直接验证筛选，而不必绕一整条 service 链。
 */
export function filterMockAuditRows(rows: AuditLogItem[], query: AuditLogQuery): AuditLogItem[] {
  const modules = query.modules?.length ? new Set(query.modules) : undefined
  const keyword = query.keyword?.trim().toLowerCase()
  const from = toDayBoundary(query.startDate, true)
  const to = toDayBoundary(query.endDate, false)
  return rows.filter((row) => {
    if (modules && !modules.has(row.module)) return false
    if (query.action && row.action !== query.action) return false
    if (query.operatorId && row.operatorId !== query.operatorId) return false
    if (query.targetType && row.targetType !== query.targetType) return false
    if (query.targetId && row.targetId !== query.targetId) return false
    if (from && row.createdAt < from) return false
    if (to && row.createdAt > to) return false
    if (keyword) {
      const haystack = [row.operatorName, row.targetName, row.summary]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      if (!haystack.includes(keyword)) return false
    }
    return true
  })
}

/** 分页读取（page 1 基，与后端 PageableUtil.oneBased 同口径）。 */
export async function listAuditLogs(query: AuditLogQuery): Promise<PaginatedResponse<AuditLogItem>> {
  await mockDelay()
  const sorted = [...readRows()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
  const matched = filterMockAuditRows(sorted, query)
  const page = Math.max(query.page ?? 1, 1)
  const pageSize = Math.min(Math.max(query.pageSize ?? 20, 1), 100)
  const start = (page - 1) * pageSize
  return {
    items: matched.slice(start, start + pageSize),
    total: matched.length,
    page,
    pageSize,
  }
}

/**
 * 字典：模块固定六个，动作只列出 mock 行里真正出现过的 code。
 *
 * <p>真实后端模式下这份由枚举全量下发；mock 模式不做全量镜像，是为了避免「改后端枚举忘了改这里」
 * 造成的两侧标签漂移，未收录的 code 前端原样显示。
 */
export async function getAuditDictionary(): Promise<AuditDictionary> {
  await mockDelay()
  const actions: AuditActionOption[] = [...new Set(readRows().map((row) => row.action))]
    .sort()
    .map((code) => ({ code, label: MOCK_ACTION_LABELS[code] ?? code, tag: 'info', level: 'info' }))
  return { modules: MOCK_MODULES, actions }
}
